// useWorkspaceSettings — the Settings→Account cells: the tier-0 display name,
// the semantic-summary consent mirror and the editable workspace name, each an
// optimistic device/draft cell plus the command dispatch that carries the real
// intent to the domain. Split out of useShellFoundation (size gate) without
// changing behaviour: called once, unconditionally, right after the library
// cell it reads, so both library-sync effects keep their registration order.
export { useWorkspaceSettings };

import { Result } from "@bilda/utils";
import { useEffect, useState } from "react";
import { readDisplayName, writeDisplayName } from "../displayName.js";
import type { ExternalError } from "../externalError.js";
import type { HostNotice } from "../frame.js";
import type { AppPorts, Library } from "../ports.js";
import { readSummaryConsent, writeSummaryConsent } from "../summaryConsent.js";

type WorkspaceSettingsDeps = {
    readonly ports: Pick<
        AppPorts,
        "setAuthorName" | "setSummaryConsent" | "renameWorkspace"
    >;
    readonly library: Library | null;
    readonly displayText: (e: ExternalError) => string;
    readonly showNotice: (n: HostNotice) => void;
};

const useWorkspaceSettings = ({
    ports,
    library,
    displayText,
    showNotice,
}: WorkspaceSettingsDeps) => {
    // The tier-0 author identity: a per-device display name (localStorage,
    // like the theme — see app/displayName.ts). Seeded once at mount;
    // persisted on every edit from the Settings account panel.
    const [displayName, setDisplayName] = useState<string>(() =>
        readDisplayName(),
    );
    // The semantic-summary consent trust surface (IDD 94442b9d): a per-device
    // mirror (localStorage, like `displayName` above — `summaryConsent.ts`),
    // since `GET /state` doesn't yet read `Workspace.summaryConsent` back
    // (flagged, `app/summaryProjection.ts`). Default off, matching the
    // domain default.
    const [summaryConsent, setSummaryConsentState] = useState<boolean>(() =>
        readSummaryConsent(),
    );
    const onDisplayNameChange = (name: string) => {
        // Optimistic device-local update (the tier-0 cache, survives reloads),
        // AND dispatch to the domain so the `author_name_set` event carries the
        // name to the agent's prompt (buildAuthorPrompt) — one name across
        // device, domain, and agent. A domain refusal surfaces as a notice; the
        // local edit stands (the device preference is never lost to a backend
        // hiccup).
        setDisplayName(name);
        writeDisplayName(name);
        const run = async () => {
            const r = await ports.setAuthorName(name);
            if (Result.isErr(r)) {
                showNotice({
                    type: "lifecycle-failed",
                    message: displayText(r.error),
                });
            }
        };
        void run();
    };

    // Same optimistic-mirror-plus-dispatch shape as `onDisplayNameChange`:
    // the device mirror is the instant-paint cache (no round-trip needed to
    // reflect the toggle immediately); the `setSummaryConsent` dispatch
    // carries the real intent to the domain (the consent gate
    // `generateSemanticSummary` reads). A domain refusal surfaces as a
    // notice and reverts the optimistic mirror — the cache must not keep
    // claiming a state the server rejected. Returns the write's promise so
    // the caller (`useAppShellHost`) can force an immediate reread once it
    // settles, confirming the server-projected truth (`library.summary.
    // consentEnabled`, synced below) without waiting for the next poll.
    const onSummaryConsentChange = (consented: boolean): Promise<void> => {
        setSummaryConsentState(consented);
        writeSummaryConsent(consented);
        const run = async () => {
            const r = await ports.setSummaryConsent(consented);
            if (Result.isErr(r)) {
                setSummaryConsentState(!consented);
                writeSummaryConsent(!consented);
                showNotice({
                    type: "lifecycle-failed",
                    message: displayText(r.error),
                });
            }
        };
        return run();
    };

    // The editable workspace name (F56). SERVER-projected truth is
    // `Library.workspaceName` ("" = unnamed); this cell is the Settings→
    // Account field's draft, seeded + self-healed from every library read
    // (same shape as `summaryConsent` above). The field's placeholder shows
    // the derived default; committing dispatches the `renameWorkspace`
    // command whose folded `workspace_renamed` event becomes the new truth.
    const [workspaceName, setWorkspaceName] = useState<string>("");
    useEffect(() => {
        if (library === null) return;
        setWorkspaceName(library.workspaceName);
    }, [library]);
    const onWorkspaceNameChange = (name: string) => {
        setWorkspaceName(name);
        const run = async () => {
            const r = await ports.renameWorkspace(name);
            if (Result.isErr(r)) {
                showNotice({
                    type: "lifecycle-failed",
                    message: displayText(r.error),
                });
            }
        };
        void run();
    };

    // The server projection is now the SOURCE OF TRUTH for consent
    // (`Library.summary.consentEnabled`, the semantic-summary READ path);
    // the localStorage-seeded `summaryConsent` cell above is only the
    // instant-paint cache for the render before the first library load
    // resolves, and the offline fallback while a read is stale. Every
    // fresh library read corrects the cell + refreshes the cache, so a
    // stale mirror (e.g. a second device toggled it) self-heals on the
    // very next read instead of lingering as a false "on"/"off".
    useEffect(() => {
        if (library === null) return;
        const real = library.summary.consentEnabled;
        setSummaryConsentState(real);
        writeSummaryConsent(real);
    }, [library]);

    return {
        displayName,
        onDisplayNameChange,
        workspaceName,
        onWorkspaceNameChange,
        summaryConsent,
        onSummaryConsentChange,
    };
};
